import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { HardHat } from "lucide-react";

import OperationsShell from "@/components/operations/OperationsShell";
import BackButton from "@/components/BackButton";
import FieldTeamAdminContent from "@/components/admin/FieldTeamAdminContent";
import { useAuth } from "@/context/AuthContext";

/**
 * FIELD-001C1A · Sole administration surface for the unified field team.
 * Renders `FieldTeamAdminContent` (shell-free content extracted from
 * EngineerAdmin) inside a SINGLE `<OperationsShell>`. This prevents the
 * previously nested `<InstallerShell>` inside `<OperationsShell>` that
 * shipped the engineer portal chrome into the admin surface.
 *
 * Route: `/operations/administration/field-team`.
 * Access still requires `manage_engineers`. The legacy
 * `/operations/engineers?tab=administration` route redirects here.
 */
export default function OperationsFieldTeam() {
    const { user, hasPermission } = useAuth();
    const canManage = typeof hasPermission === "function"
        ? hasPermission("manage_engineers")
        : (Array.isArray(user?.permissions) && user.permissions.includes("manage_engineers"));

    return (
        <OperationsShell>
            <div className="min-h-screen" data-testid="ops-field-team-page">
                <div className="mx-auto max-w-6xl px-4 md:px-8 pt-6 md:pt-10 pb-8">
                    <BackButton fallbackHref="/operations/administration"
                                testid="ops-field-team-back" className="mb-4" />
                    <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-xl bg-[#E1E6F0] text-[#131B2F] flex items-center justify-center shrink-0">
                            <HardHat size={22} strokeWidth={1.75} />
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.14em] text-[#4B5565] font-medium">
                                Operations · Administration
                            </div>
                            <h1 className="text-2xl md:text-3xl text-[#131B2F] font-semibold">
                                Field Team Management
                            </h1>
                            <p className="mt-2 text-sm text-[#4B5565] max-w-2xl">
                                Add, edit, archive and classify internal people, external
                                contractors and external organisations. This is the only
                                administration surface for the field team.
                            </p>
                        </div>
                    </div>
                </div>
                {canManage ? (
                    <FieldTeamAdminContent />
                ) : (
                    <div className="mx-auto max-w-3xl px-4 md:px-8 pb-16"
                         data-testid="ops-field-team-no-perm">
                        <div className="rounded-xl border border-[#E5E7EB] bg-white p-8 text-sm text-[#4B5565]">
                            You do not have permission to administer the field team.
                            <div className="mt-3">
                                <Link to="/operations/resources"
                                      className="text-[#131B2F] underline"
                                      data-testid="ops-field-team-goto-resources">
                                    <ArrowLeft size={12} className="inline mr-1" />
                                    Back to Resource Management
                                </Link>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </OperationsShell>
    );
}
